import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'ibclub_chat_session';

const getOrCreateSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const WELCOME_MSG = {
  role: 'assistant',
  content:
    "Namaste! I'm IB Assistant. Ask me about membership tiers, events, referrals, or anything about IB Club India.",
};

const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getOrCreateSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId.current,
        message: text,
      }, { withCredentials: true });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "I couldn't reach the assistant right now. Please email membership@ibclub.org or try again shortly.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const quickPrompts = [
    'Tell me about Elite membership',
    'How does the referral program work?',
    'Upcoming events in Mumbai',
  ];

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="open-ai-chat-button"
          className="fixed bottom-6 left-6 z-50 group"
        >
          <div className="absolute inset-0 rounded-full bg-[#d4af37] blur-xl opacity-40 group-hover:opacity-70 transition-opacity"></div>
          <div className="relative bg-gradient-to-br from-[#d4af37] to-[#b8941f] text-slate-950 rounded-full p-4 shadow-2xl border border-[#d4af37]/40 flex items-center gap-2 group-hover:scale-105 transition-transform">
            <Sparkles size={20} />
            <span className="hidden md:block text-sm font-semibold pr-2">Ask IB Assistant</span>
          </div>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-6 left-6 z-50 w-[92vw] max-w-md h-[70vh] max-h-[600px] bg-slate-950 border border-[#d4af37]/30 shadow-2xl flex flex-col rounded-lg overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#d4af37] rounded-full flex items-center justify-center">
                <Sparkles size={18} className="text-slate-950" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">IB Assistant</p>
                <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                  Online · AI-powered
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="close-ai-chat-button"
              className="text-slate-400 hover:text-white p-1"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="ai-chat-messages"
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-950"
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-lg text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#d4af37] text-slate-950'
                      : 'bg-slate-800 text-slate-100 border border-white/5'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-slate-800 text-slate-400 px-4 py-2.5 rounded-lg text-sm border border-white/5">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce"></span>
                    <span
                      className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce"
                      style={{ animationDelay: '0.15s' }}
                    ></span>
                    <span
                      className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-bounce"
                      style={{ animationDelay: '0.3s' }}
                    ></span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts (only show when only welcome message) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {quickPrompts.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setInput(q);
                  }}
                  className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-full border border-white/5 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={handleSend}
            className="p-3 border-t border-white/10 bg-slate-900 flex gap-2"
            data-testid="ai-chat-form"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about IB Club..."
              data-testid="ai-chat-input"
              className="flex-1 bg-slate-800 border border-white/10 focus:border-[#d4af37] text-white text-sm px-4 py-2.5 rounded outline-none transition-colors"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="ai-chat-send-button"
              className="bg-[#d4af37] hover:bg-[#e5c558] text-slate-950 px-4 rounded font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
